import type { UserGroup } from '@gmn-portal/backend';
import { nanoid } from 'nanoid';
import { DEFAULT_CUSTOMER_GROUP_KEYS, DEFAULT_GMN_GROUP_KEYS } from '@/shared/utils/permissions';
import type {
  FieldError,
  FieldPermission,
  FieldPermissions,
  FieldType,
  FieldWidth,
  JsonSchemaType,
  MetadataBuilderState,
  MetadataField,
  MetadataGroup,
  StatusOption,
} from './types';

const ensureStatusOptionIds = (statusOptions?: StatusOption[]): StatusOption[] | undefined => {
  if (!statusOptions) return undefined;

  return statusOptions.map((option, index) => ({
    ...option,
    id: option.id?.trim() || `status-opt-${option.color}-${index + 1}`,
  }));
};

const sanitizeEnumOptions = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const options = Array.from(
    new Set(
      value
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0)
    )
  );

  return options.length > 0 ? options : undefined;
};

/**
 * Convert a JsonSchemaType to the internal MetadataBuilderState
 */
export function jsonSchemaToBuilderState(
  schema?: JsonSchemaType,
  userGroups?: UserGroup[]
): MetadataBuilderState {
  if (!schema) {
    return {
      groups: [createDefaultGroup()],
      fields: [],
    };
  }

  const resolvedUserGroups =
    userGroups && userGroups.length > 0
      ? userGroups
      : [
          ...DEFAULT_GMN_GROUP_KEYS.map((key) => ({ key, domain: 'gmn' as const })),
          ...DEFAULT_CUSTOMER_GROUP_KEYS.map((key) => ({ key, domain: 'customer' as const })),
        ];

  const groups: MetadataGroup[] = schema.groups?.length
    ? schema.groups.map((g, index) => ({
        ...g,
        order: g.order ?? index + 1,
      }))
    : [createDefaultGroup()];

  const fields: MetadataField[] = schema.propertyOrder.map((key, index) => {
    const prop = schema.properties[key];
    // Extract positions-specific fields using index access (they're in the [k: string]: unknown part)
    const statusLabel = prop.statusLabel as string | undefined;
    const statusLabelEn = prop.statusLabelEn as string | undefined;

    // Extract status-specific fields
    const statusOptions = ensureStatusOptionIds(prop.statusOptions as StatusOption[] | undefined);
    const isGroupIndicator = prop.isGroupIndicator as boolean | undefined;
    const icon = prop.icon as string | undefined;
    const filterFavorite = prop.filterFavorite === true;
    const highlighted = prop.highlighted === true;
    const relationDisplayVariant = prop.relationDisplayVariant as
      | 'ship-linked-windpark-helicopters'
      | 'windpark-linked-ship-medics'
      | undefined;
    const sourceContactFieldKeys =
      prop.type === 'relation-display' && Array.isArray(prop.sourceContactFieldKeys)
        ? prop.sourceContactFieldKeys.filter((entry): entry is string => typeof entry === 'string')
        : undefined;
    const enumOptions = prop.type === 'string' ? sanitizeEnumOptions(prop.enum) : undefined;

    const permissions = resolveFieldPermissions({
      prop,
      userGroups: resolvedUserGroups,
    });

    return {
      id: key,
      key,
      labelDe: prop.description,
      labelEn: prop.description_en || '',
      type: prop.type === 'string' && prop.format === 'date-time' ? 'date' : prop.type,
      required: schema.required.includes(key),
      permissions,
      filterFavorite,
      highlighted,
      groupId: prop.groupId || groups[0]?.id || '',
      width: (prop.width || '100') as FieldWidth,
      order: index + 1,
      ...(prop.type === 'string' && enumOptions ? { enum: enumOptions } : {}),
      // Positions-specific fields
      ...(prop.type === 'positions' && statusLabel ? { statusLabel } : {}),
      ...(prop.type === 'positions' && statusLabelEn ? { statusLabelEn } : {}),
      // Status-specific fields
      ...(prop.type === 'status' && statusOptions ? { statusOptions } : {}),
      ...(prop.type === 'status' && isGroupIndicator !== undefined ? { isGroupIndicator } : {}),
      ...(prop.type === 'status' && icon ? { icon } : {}),
      ...(prop.type === 'relation-display' && relationDisplayVariant
        ? { relationDisplayVariant }
        : {}),
      ...(prop.type === 'relation-display' && sourceContactFieldKeys?.length
        ? { sourceContactFieldKeys }
        : {}),
    };
  });

  return { groups, fields };
}

type LegacyCustomerAccess = 'none' | 'read' | 'read-and-write' | undefined;

const legacyCustomerAccessToPermission = (access: LegacyCustomerAccess): FieldPermission => {
  if (access === 'read-and-write') {
    return { read: true, update: true };
  }
  if (access === 'read') {
    return { read: true, update: false };
  }
  return { read: false, update: false };
};

const resolveFieldPermissions = ({
  prop,
  userGroups,
}: {
  prop: JsonSchemaType['properties'][string];
  userGroups: Array<{ key: string; domain: 'gmn' | 'customer' }>;
}): FieldPermissions => {
  const record = (prop.permissions ?? {}) as Record<string, FieldPermission>;
  const groupKeys = [...new Set([...userGroups.map((group) => group.key), ...Object.keys(record)])];

  const result: FieldPermissions = {};
  groupKeys.forEach((key) => {
    const entry = record[key];
    if (entry) {
      result[key] = {
        read: Boolean(entry.read),
        update: Boolean(entry.update),
      };
      return;
    }

    const group = userGroups.find((g) => g.key === key);
    if (group?.domain === 'customer' && prop.customerAccess) {
      result[key] = legacyCustomerAccessToPermission(prop.customerAccess);
      return;
    }

    result[key] = { read: false, update: false };
  });

  return result;
};

/**
 * Convert the internal MetadataBuilderState back to JsonSchemaType
 */
export function builderStateToJsonSchema(state: MetadataBuilderState): JsonSchemaType {
  const sortedGroups = [...state.groups].sort((a, b) => a.order - b.order);
  const sortedFields = [...state.fields].sort((a, b) => a.order - b.order);

  const properties: JsonSchemaType['properties'] = {};
  const required: string[] = [];
  const propertyOrder: string[] = [];

  for (const field of sortedFields) {
    const key = field.key.startsWith('temp-')
      ? `${toCamelCase(field.labelDe)}-${nanoid(8)}`
      : field.key;

    if (field.type === 'date') {
      properties[key] = {
        type: 'string',
        format: 'date-time',
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        ...(field.filterFavorite ? { filterFavorite: true } : {}),
        ...(field.highlighted ? { highlighted: true } : {}),
        groupId: field.groupId,
        width: field.width,
      };
    } else if (field.type === 'contact') {
      // Contact fields store the full nested schema
      // Contact fields are always optional at top-level to comply with full-sync logic
      properties[key] = {
        type: 'contact',
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        ...(field.highlighted ? { highlighted: true } : {}),
        groupId: field.groupId,
        width: '100', // Contact fields always use full width
      };
    } else if (field.type === 'coordinate') {
      properties[key] = {
        type: 'coordinate',
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        groupId: field.groupId,
        width: '100',
      };
    } else if (field.type === 'positions') {
      // Positions fields store array of coordinates
      // Positions fields are always optional at top-level to comply with full-sync logic
      properties[key] = {
        type: 'positions',
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        ...(field.highlighted ? { highlighted: true } : {}),
        groupId: field.groupId,
        width: '100', // Positions fields always use full width
        ...(field.statusLabel && { statusLabel: field.statusLabel }),
        ...(field.statusLabelEn && { statusLabelEn: field.statusLabelEn }),
      };
    } else if (field.type === 'status') {
      // Status fields store traffic light status with optional time-based scheduling
      // Status fields are always optional at top-level to comply with full-sync logic
      properties[key] = {
        type: 'status',
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        ...(field.filterFavorite ? { filterFavorite: true } : {}),
        ...(field.highlighted ? { highlighted: true } : {}),
        groupId: field.groupId,
        width: '100', // Status fields always use full width
        ...(field.statusOptions && { statusOptions: field.statusOptions }),
        ...(field.isGroupIndicator && { isGroupIndicator: field.isGroupIndicator }),
        ...(field.icon && { icon: field.icon }),
      };
    } else if (field.type === 'relation-display') {
      properties[key] = {
        type: 'relation-display',
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        groupId: field.groupId,
        width: '100',
        relationDisplayVariant: field.relationDisplayVariant,
        ...(field.sourceContactFieldKeys?.length
          ? { sourceContactFieldKeys: field.sourceContactFieldKeys }
          : {}),
      };
    } else if (field.type === 'string') {
      properties[key] = {
        type: field.type,
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        ...(field.filterFavorite ? { filterFavorite: true } : {}),
        ...(field.highlighted ? { highlighted: true } : {}),
        groupId: field.groupId,
        width: field.width,
        ...(sanitizeEnumOptions(field.enum) ? { enum: sanitizeEnumOptions(field.enum) } : {}),
        ...(field.required && { minLength: 1 }),
      };
    } else {
      properties[key] = {
        type: field.type,
        description: field.labelDe,
        description_en: field.labelEn || undefined,
        permissions: compactPermissions(field.permissions),
        ...(field.filterFavorite ? { filterFavorite: true } : {}),
        ...(field.highlighted ? { highlighted: true } : {}),
        groupId: field.groupId,
        width: field.width,
      };
    }

    // Sections, contacts, positions, and status cannot be required (due to full-sync logic)
    if (
      field.required &&
      field.type !== 'boolean' &&
      field.type !== 'section' &&
      field.type !== 'contact' &&
      field.type !== 'coordinate' &&
      field.type !== 'positions' &&
      field.type !== 'status' &&
      field.type !== 'relation-display'
    ) {
      required.push(key);
    }

    propertyOrder.push(key);
  }

  return {
    type: 'object',
    groups: sortedGroups,
    properties,
    required,
    propertyOrder,
  };
}

const compactPermissions = (permissions: FieldPermissions) => {
  const compact: FieldPermissions = {};
  Object.entries(permissions).forEach(([key, value]) => {
    if (value.read || value.update) {
      compact[key] = { read: value.read, update: value.update };
    }
  });
  return compact;
};

/**
 * Create a new default group
 */
export function createDefaultGroup(): MetadataGroup {
  return {
    id: `group-${nanoid(8)}`,
    labelDe: 'Allgemein',
    labelEn: 'General',
    order: 1,
  };
}

/**
 * Create a new group with incremented order
 */
export function createNewGroup(existingGroups: MetadataGroup[]): MetadataGroup {
  const maxOrder = Math.max(0, ...existingGroups.map((g) => g.order));
  return {
    id: `group-${nanoid(8)}`,
    labelDe: 'Neue Gruppe',
    labelEn: 'New Group',
    order: maxOrder + 1,
  };
}

/**
 * Create a new field with default values
 */
export function createNewField(
  groupId: string,
  existingFields: MetadataField[],
  type: FieldType = 'string'
): MetadataField {
  const groupFields = existingFields.filter((f) => f.groupId === groupId);
  const maxOrder = Math.max(0, ...groupFields.map((f) => f.order));

  const isFullWidthType =
    type === 'section' ||
    type === 'contact' ||
    type === 'coordinate' ||
    type === 'positions' ||
    type === 'status' ||
    type === 'relation-display';

  return {
    id: `temp-${nanoid(8)}`,
    key: `temp-${nanoid(8)}`,
    labelDe: '',
    labelEn: '',
    type,
    required: false,
    permissions: {},
    filterFavorite: false,
    highlighted: false,
    groupId,
    width: isFullWidthType ? '100' : '50',
    order: maxOrder + 1,
  };
}

/**
 * Convert string to camelCase for field keys
 */
function toCamelCase(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Reorder items in an array (for drag and drop)
 */
export function reorderArray<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const result = Array.from(array);
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}

/**
 * Update order values after reordering
 */
export function updateOrderValues<T extends { order: number }>(items: T[]): T[] {
  return items.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
}

/**
 * Parse backend API errors into field errors
 * This handles the specific error codes returned by the metadata validation
 */
interface ParsedBackendError {
  code: string;
  field: string;
  message: string;
  details?: {
    fields?: string[];
    oldType?: string;
    newType?: string;
  };
}

/**
 * Parse backend error response and return field-specific errors.
 * This function extracts validation errors from the Payload CMS error response
 * and maps them to specific fields in the builder.
 *
 * @param error - The error from the API call
 * @param t - Translation function (must accept metadata.fieldMustBeOptional and metadata.fieldTypeChangeNotAllowed)
 */
export function parseMetadataApiErrors(
  error: unknown,
  t: (key: 'metadata.fieldMustBeOptional' | 'metadata.fieldTypeChangeNotAllowed') => string
): FieldError[] {
  const errors: FieldError[] = [];

  const payloadError = error as {
    response?: {
      errors?: Array<{
        name?: string;
        data?: {
          errors?: ParsedBackendError[];
        };
        message?: string;
      }>;
    };
  };

  if (!payloadError.response?.errors) {
    return errors;
  }

  for (const err of payloadError.response.errors) {
    if (err.data?.errors) {
      for (const apiError of err.data.errors) {
        switch (apiError.code) {
          case 'NewRequiredFieldAdditionNotAllowed':
          case 'ExistingFieldMadeRequiredWithMissingValues':
            // These errors contain a list of affected fields
            if (apiError.details?.fields) {
              for (const field of apiError.details.fields) {
                errors.push({
                  fieldKey: field,
                  errorType: 'required',
                  message: t('metadata.fieldMustBeOptional'),
                });
              }
            }
            break;

          case 'FieldTypeChangeNotAllowed':
            // This error is for a single field
            errors.push({
              fieldKey: apiError.field,
              errorType: 'type',
              message: t('metadata.fieldTypeChangeNotAllowed'),
            });
            break;

          default:
            // Generic error - could be shown as toast instead
            break;
        }
      }
    }
  }

  return errors;
}

/**
 * Get a set of group IDs that have fields with errors
 */
export function getGroupsWithErrors(
  fields: MetadataField[],
  fieldErrors: FieldError[]
): Set<string> {
  const groupsWithErrors = new Set<string>();

  for (const error of fieldErrors) {
    const field = fields.find((f) => f.key === error.fieldKey || f.id === error.fieldKey);
    if (field) {
      groupsWithErrors.add(field.groupId);
    }
  }

  return groupsWithErrors;
}
